import { useMemo, useCallback } from 'react';
import { MaintenanceRecordCreate } from '../../../../../types/api';
import { useFormState } from '../../../../../hooks/useFormState';
import {
  useMaintenanceTypesQuery,
  useMaintenanceOrganizationUsersQuery,
} from '../../maintenance.queries';
import { useCreateMaintenanceTypeMutation } from '../../maintenance.mutations';

const initialRecord: MaintenanceRecordCreate = {
  drone_serial: '',
  maintenance_types: [],
  work_description: '',
  start_date: new Date().toISOString().split('T')[0],
  end_date: new Date().toISOString().split('T')[0],
  status: 'planned',
  quality_checked: false,
  performed_by: '',
  attachments: [],
};

interface UseMaintenanceFormStateProps {
  open: boolean;
  droneSerial: string;
}

export const useMaintenanceFormState = ({ open, droneSerial }: UseMaintenanceFormStateProps) => {
  // STD-016: server reads go through TanStack queries (enabled only while the
  // dialog is open); inline type creation goes through a mutation that
  // invalidates the types query so the new option appears.
  const typesQuery = useMaintenanceTypesQuery(open);
  const usersQuery = useMaintenanceOrganizationUsersQuery(open);
  const createTypeMutation = useCreateMaintenanceTypeMutation();

  // Memoize initial data to prevent unnecessary resetForm recreations
  const initialData = useMemo(
    () => ({
      ...initialRecord,
      drone_serial: droneSerial,
    }),
    [droneSerial]
  );

  // Use the generic form state hook with validation rules
  const {
    formData: record,
    updateField: handleFieldChange,
    isValid,
  } = useFormState<MaintenanceRecordCreate>({
    initialData,
    validationRules: [
      {
        field: 'maintenance_types',
        validate: (value) => Array.isArray(value) && value.length > 0,
        errorMessage: 'At least one maintenance type is required',
      },
      {
        field: 'work_description',
        validate: (value) => typeof value === 'string' && Boolean(value.trim()),
        errorMessage: 'Work description is required',
      },
      {
        field: 'start_date',
        validate: (value) => Boolean(value),
        errorMessage: 'Start date is required',
      },
      {
        field: 'end_date',
        validate: (value, formData) => {
          if (!value) return false;
          if (!formData.start_date) return true;
          return value >= formData.start_date;
        },
        errorMessage: 'End date must be after start date',
      },
      {
        field: 'performed_by',
        validate: (value) => typeof value === 'string' && Boolean(value.trim()),
        errorMessage: 'Performed by is required',
      },
    ],
    resetOnOpen: true,
    openTrigger: open,
  });

  // Add a new maintenance type — the mutation invalidates the types query so the
  // refetched list includes the new option. Returns the new { value, label } so
  // the form can auto-select it (MaintenanceFormFields relies on this).
  const addMaintenanceType = useCallback(
    async (label: string) => {
      const newType = await createTypeMutation.mutateAsync(label);
      return { value: newType.value, label: newType.label };
    },
    [createTypeMutation]
  );

  // For backward compatibility, maintain the same interface
  const isFormValid = isValid && record.start_date <= record.end_date;

  return {
    record,
    maintenanceTypes: typesQuery.data ?? null,
    loadingTypes: typesQuery.isFetching,
    organizationUsers: usersQuery.data ?? [],
    loadingUsers: usersQuery.isFetching,
    handleFieldChange,
    isFormValid,
    addMaintenanceType,
  };
};
